
import React, { Component } from 'react';
import { connect } from 'react-redux';
import assign from 'lodash/assign';
import get from 'lodash/get';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { mergeStyleSets } from 'office-ui-fabric-react/lib/Styling';

import {
    sagas as sagasMap
} from '../../map';
import { sagas as sagasData, DataWindow } from '../../data';
import { sagas as sagasMapMultiScene, MapMultiScene } from '../../mapmultiscene';
import { sagas as sagasMultiScene } from '../../multiscene';
import { sagas as sagasMeasurement } from '../../measurement';
import appGetActions from '../../app/saga';
import { getMapLayers } from '../../project';
import TabPanelsViewer from './TabPanelsViewer';
import getAppModules from './getAppModules';
import getTabsModule from './getTabsModule';
import getWindowList from './getWindowList';
import showObjectsInfo from './showObjectsInfo';
import WindowSwitcher from './WindowSwitcher';

/**
 * Viewer containing 2D map window, 3D scene window and panel with tools.
 *
 * @reactProps {Object} projectDefinition - project definition
 * @reactProps {Object} appLayoutProps - properties for customize layout
 * @reactProps {Object} map - actual map state
 */
class MapMultiSceneViewer extends Component {

    classNames = mergeStyleSets({
        mapMultiScene: {
            borderLeft: '1px solid #cccccc',
            borderRight: '1px solid #cccccc'
        }
    });

    constructor(props) {
        super(props);
        this._mapMultiSceneComponent = null;
    }

    getAppModules() {
        return getAppModules(this.props.projectDefinition);
    }

    getAvailablePanels() {
        const panels = ['data', 'layer', 'measurement', 'print', 'search', 'share', 'user'];
        const hasHomeContent = get(this.props, 'projectDefinition.app.home.content');
        return hasHomeContent ? ['home', ...panels] : panels;
    }

    getLayout() {
        let layout = get(this.props, 'projectDefinition.app.layout', null);
        if (!layout) {
            layout = get(this.props, 'layout', {});
        }
        assign(layout, this.props.appLayoutProps);
        return layout;
    }

    getLayoutValue(name, defaultValue) {
        let layout = this.getLayout();
        return get(layout, name, defaultValue);
    }

    getLayoutValues() {
        return {
            layersPanelPosition: this.getLayoutValue('layersPanelPosition', 'left'),
            multiScenePanelPosition: this.getLayoutValue('multiScenePanelPosition', 'left'),
            tabPanelsWidth: this.getLayoutValue('tabPanelsWidth', null),
            useStandaloneScene: this.getLayoutValue('useStandaloneScene', false)
        }
    }

    getTabs() {
        return getTabsModule(
            this.getAppModules(),
            this.getAvailablePanels(),
            !!this.props.map,
            (this.props.auth.data && this.props.auth.data.user)
        );
    }

    getWindowSwitcher() {
        const windowList = getWindowList(this.getAppModules());

        return (
            <WindowSwitcher
                activeWindow="map"
                appActions={this.props.appActions}
                windowList={windowList}
            />
        );
    }

    handleChangeView = (center, zoom, rotation, resolution) => {
        this.props.mapActions.changeView(center, zoom, rotation, resolution);
    }

    handleObjectInfo = async (layerId, attributes, sourceType) => {
        showObjectsInfo({
            appActions: this.props.appActions,
            appModules: this.getAppModules(),
            appConfig: this.props.projectDefinition.app,
            dataActions: this.props.dataActions,
            finished: true,
            objects: [{
                attributes,
                layerId
            }],
            mapLayers: getMapLayers(this.props.project),
            sourceType
        });
    }

    handleObjectsInfo = async (objects, finished, coordinates) => {
        showObjectsInfo({
            appActions: this.props.appActions,
            appModules: this.getAppModules(),
            appConfig: this.props.projectDefinition.app,
            dataActions: this.props.dataActions,
            finished,
            objects,
            mapLayers: getMapLayers(this.props.project),
            coordinates
        });
    }

    handleShowMessage = (message) => {
        showObjectsInfo({
            appActions: this.props.appActions,
            appModules: this.getAppModules(),
            appConfig: this.props.projectDefinition.app,
            dataActions: this.props.dataActions,
            message,
        });
    }

    render() {
        return (
            <Stack horizontal styles={{root: {height: '100%'}}} tokens={{childrenGap: 10, padding: 5}}>
                {this.renderContent(this.getLayoutValues())}
            </Stack>
        );
    }

    renderContent(layoutValues) {
        const activeWindow = this.props.uiValues['activeWindow'] || 'map';
        switch (activeWindow) {
            case 'data':
                return this.renderContentDataWindow(layoutValues);
            default:
                return this.renderContentMapWindow(layoutValues);
        }
    }

    renderContentDataWindow(layoutValues) {
        return (
            <Stack.Item grow>
                <DataWindow
                    appActions={this.props.appActions}
                    appConfig={this.props.projectDefinition.app}
                    tabs={this.getTabs()}
                    windowList={getWindowList(this.getAppModules())}
                />
            </Stack.Item>
        );
    }

    renderContentMapWindow(layoutValues) {
        let tabPanelsComponent = this.renderTabPanels(layoutValues);
        let mapMultiScene = this.renderMapMultiScene(layoutValues);
        let leftComponent = tabPanelsComponent;
        let rightComponent = mapMultiScene;
        if (layoutValues['layersPanelPosition'] === 'right') {
            leftComponent = mapMultiScene;
            rightComponent = tabPanelsComponent;
        }
        return (
            <React.Fragment>
                {leftComponent}
                {rightComponent}
            </React.Fragment>
        );
    }

    renderTabPanels(layoutValues) {
        return (
            <Stack.Item>
                <TabPanelsViewer
                    additionalContent={this.getWindowSwitcher()}
                    appModules={this.getAppModules()}
                    appActions={this.props.appActions}
                    appConfig={this.props.projectDefinition.app}
                    layoutValues={layoutValues}
                    map={this.props.map}
                    mapActions={this.props.mapActions}
                    onObjectInfo={this.handleObjectInfo}
                    tabs={this.getTabs()}
                    uiValues={this.props.uiValues}
                />
            </Stack.Item>
        );
    }

    renderMapMultiScene(layoutValues) {
        return (
            <Stack.Item grow={1} className={this.classNames.mapMultiScene}>
                <MapMultiScene
                    ref={element => this._mapMultiSceneComponent = element}
                    appConfig={this.props.projectDefinition.app}
                    multiSceneProject={this.props.projectDefinition.multiSceneProject}
                    map={this.props.map}
                    layers={this.props.map.layers}
                    groups={this.props.map.groups}
                    mapMultiScene={this.props.mapMultiScene}
                    multiScene={this.props.multiScene}
                    layoutValues={layoutValues}
                    onChangeView={this.handleChangeView}
                    onObjectInfo={this.handleObjectInfo}
                    onObjectsInfo={this.handleObjectsInfo}
                    onShowMessage={this.handleShowMessage}
                    mapMultiSceneActions={this.props.mapMultiSceneActions}
                    multiSceneActions={this.props.multiSceneActions}
                    appActions={this.props.appActions}
                    mapActions={this.props.mapActions}
                    measurementActions={this.props.measurementActions}
                    urlParams={this.props.urlParams}
                    project={this.props.project}
                />
            </Stack.Item>
        );
    }
}

function mapDispatchToProps(dispatch) {
    return {
        dataActions: sagasData.getActions(dispatch),
        mapActions: sagasMap.getActions(dispatch),
        mapMultiSceneActions: sagasMapMultiScene.getActions(dispatch),
        multiSceneActions: sagasMultiScene.getActions(dispatch),
        measurementActions: sagasMeasurement.getActions(dispatch),
        appActions: appGetActions(dispatch)
    };
}

function mapStateToProps(state, ownProps) {
    return {
        ...ownProps,
        auth: state.auth,
        uiValues: state.app.uiValues,
        urlParams: state.app.urlParams,
        mapMultiScene: state.mapMultiScene.mapMultiScene,
        multiScene: state.multiScene.multiScene,
        measurement: state.measurement,
        project: state.project
    };
}

const ConnectedMapMultiSceneViewer = connect(mapStateToProps, mapDispatchToProps)(MapMultiSceneViewer);

export default ConnectedMapMultiSceneViewer;
